// Motion layer for the board's results list. Each item is wrapped in a plain
// motion div; the rows inside are untouched. Only transform and opacity
// animate, so rows stay in normal flow and in the DOM.
//
// - New items drop in from 32px above, 50ms apart (only items that were not
//   on screen last render; items that stay spring to their new spot).
// - Leaving items fade out fast with a slight scale-down and are taken out
//   of flow at once, so the rows below can spring up while they fade.
// - Reduced motion, bots and headless renderers (the prerender worker) get
//   no animation, so an SEO snapshot never catches a half-faded row.
import { useEffect, useRef, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion, type Transition } from "framer-motion";

const ENTER_OFFSET_PX = 32;
const STAGGER_S = 0.05;
const MAX_STAGGER_S = 0.6; // a full page of 25 new rows finishes inside ~1s
const REFLOW_SPRING: Transition = { type: "spring", stiffness: 210, damping: 24 };
const EXIT = { opacity: 0, scale: 0.97, transition: { duration: 0.15, ease: "easeIn" } } as const;

const isAutomated = () => {
  if (typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & { webdriver?: boolean };
  return nav.webdriver === true || /bot|crawl|spider|slurp|headless|lighthouse|prerender/i.test(nav.userAgent);
};

export interface CascadeItem {
  key: string;
  node: ReactNode;
}

export default function CascadeList({ items }: { items: CascadeItem[] }) {
  const reduceMotion = useReducedMotion();
  const still = reduceMotion || isAutomated();

  // Keys on screen after the last render; anything else is entering.
  const prevKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    prevKeysRef.current = new Set(items.map((i) => i.key));
  });

  if (still) {
    return <>{items.map((i) => <div key={i.key}>{i.node}</div>)}</>;
  }

  let enterOrder = 0;
  return (
    <AnimatePresence mode="popLayout">
      {items.map((item) => {
        const isNew = !prevKeysRef.current.has(item.key);
        const delay = isNew ? Math.min(enterOrder++ * STAGGER_S, MAX_STAGGER_S) : 0;
        return (
          <motion.div
            key={item.key}
            layout="position"
            initial={{ opacity: 0, y: -ENTER_OFFSET_PX }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={EXIT}
            transition={{
              layout: REFLOW_SPRING,
              y: { ...REFLOW_SPRING, delay },
              opacity: { duration: 0.25, delay },
            }}
          >
            {item.node}
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
}
